/**
 * Resumo executivo (redesign admin, Fase 2): a leitura de uma tela só para a
 * diretoria — o que vendeu, quanto, o que está parado e onde está o gargalo.
 *
 * Tudo é derivado dos pedidos unificados (todos os canais) e do dataset do
 * dashboard, pelos mesmos seletores das demais telas: nenhum número novo é
 * calculado "só para esta tela", e fonte ausente é declarada (nunca zero).
 * Sem metas, margens "de premissa" ou séries por período: o modelo não tem
 * essas fontes e a regra do projeto é não inventar dado.
 */
import { createElement as h, type ReactElement } from "react";
import { Link } from "react-router-dom";
import { CircleDollarSign, Clock3, MessageCircleWarning, Receipt, ShoppingBag } from "lucide-react";
import { DatasetBoundary } from "../components/DatasetBoundary.js";
import { PeriodOrdersBoundary } from "../components/PeriodOrdersBoundary.js";
import { TopProductsPanel } from "../components/TopProductsPanel.js";
import { StatusPanel } from "../components/StatusPanel.js";
import { AWAITING_CONTRACT_LABEL, buildCommandCenterStats, type DashboardDataset } from "../dashboard/kpis.js";
import { formatBrl, formatCount, formatDateTime } from "../format.js";
import { isOrdersSourceAwaitingContract, type OrdersDataset } from "../orders/dataset.js";
import { openOrders, revenueSummary } from "../orders/list-view.js";
import { salesChannelBuckets } from "../orders/channels.js";
import { ORDER_ORIGIN_LABELS, ORDER_STATE_LABELS } from "../orders/state-machine.js";
import { Card, CardHeader, EmptyState, PageHeader, StatTile } from "../ui/index.js";

export interface ResumoExecutivoViewProps {
  dataset: DashboardDataset;
  orders?: OrdersDataset;
  /** Barra de período/atualização já montada. */
  toolbar?: ReactElement;
}

export function ResumoExecutivoView({ dataset, orders, toolbar }: ResumoExecutivoViewProps): ReactElement {
  const header = h(PageHeader, {
    title: "Resumo executivo",
    description: "Números da diretoria: receita, ticket, fila em aberto e atendimento."
  });

  if (!orders || isOrdersSourceAwaitingContract(orders)) {
    return h(
      "div",
      { className: "page page--resumo flex flex-col gap-6", "data-page": "resumo" },
      header,
      h(StatusPanel, {
        kind: "awaiting_source",
        title: `Resumo de pedidos multicanal ${AWAITING_CONTRACT_LABEL}`,
        detail: "A rota /api/platform/orders não respondeu neste ambiente; sem ela não há resumo de todos os canais."
      })
    );
  }

  const summary = revenueSummary(orders.orders);
  const open = openOrders(orders.orders);
  const channels = salesChannelBuckets(orders.orders);
  const stats = buildCommandCenterStats(dataset);

  return h(
    "div",
    { className: "page page--resumo flex flex-col gap-6", "data-page": "resumo" },
    header,
    toolbar ?? null,
    h(
      "section",
      { "aria-label": "Indicadores do resumo", className: "grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3" },
      h(StatTile, {
        label: "Faturamento",
        value: formatBrl(summary.revenueCents),
        hint: "Pedidos que valem venda (pagos até entregues), todos os canais.",
        icon: CircleDollarSign,
        "data-summary": "faturamento"
      }),
      h(StatTile, {
        label: "Pedidos de receita",
        value: formatCount(summary.revenueOrders),
        icon: ShoppingBag,
        "data-summary": "pedidos-receita"
      }),
      h(StatTile, {
        label: "Ticket médio",
        value: summary.averageTicketCents === null ? "—" : formatBrl(summary.averageTicketCents),
        hint: summary.averageTicketCents === null ? "Sem pedidos de receita ainda." : undefined,
        icon: Receipt,
        "data-summary": "ticket-medio"
      }),
      h(StatTile, {
        label: "Pedidos em aberto agora",
        value: formatCount(open.length),
        hint: "Da cobrança ao faturamento.",
        icon: Clock3,
        "data-summary": "em-aberto"
      }),
      h(StatTile, {
        label: "Aguardando humano",
        value: formatCount(stats.pendingHumanTakeover),
        hint: "Conversas que a IA passou para atendimento.",
        icon: MessageCircleWarning,
        "data-summary": "aguardando-humano"
      })
    ),
    h(TopProductsPanel, { orders: orders.orders, limit: 5 }),
    h(
      "div",
      { className: "grid grid-cols-1 gap-4 lg:grid-cols-2" },
      h(
        Card,
        { "data-panel": "canais" },
        h(CardHeader, { title: "Canais de venda", description: "Pedidos e receita por canal de venda." }),
        channels.length
          ? h(
              "ul",
              { className: "m-0 flex list-none flex-col divide-y divide-border p-0" },
              channels.map((bucket) =>
                h(
                  "li",
                  { key: bucket.key, className: "flex items-center justify-between gap-3 py-2 text-sm" },
                  h("span", { className: "font-semibold text-ink" }, bucket.label),
                  h("span", { className: "text-muted" }, `${formatCount(bucket.count)} pedido(s)`),
                  h("span", { className: "font-bold tabular-nums text-ink" }, formatBrl(bucket.revenueCents))
                )
              )
            )
          : h(EmptyState, { title: "Nenhum pedido ainda" })
      ),
      h(
        Card,
        { "data-panel": "fila" },
        h(CardHeader, {
          title: "Fila em aberto",
          description: "Pedidos mais recentes ainda em andamento.",
          actions: h(Link, { to: "/pedidos", className: "text-sm font-semibold text-brand-text" }, "Ver todos")
        }),
        open.length
          ? h(
              "ul",
              { className: "m-0 flex list-none flex-col divide-y divide-border p-0" },
              [...open]
                .sort((a, b) => b.placedAt.localeCompare(a.placedAt))
                .slice(0, 5)
                .map((order) =>
                  h(
                    "li",
                    { key: order.id, className: "flex items-center justify-between gap-3 py-2 text-sm" },
                    h(Link, { to: `/pedidos/${order.id}`, className: "font-semibold text-brand-text" }, order.id),
                    h("span", { className: "text-muted" }, `${ORDER_ORIGIN_LABELS[order.origin]} · ${ORDER_STATE_LABELS[order.state]}`),
                    h("span", { className: "text-muted" }, formatDateTime(order.placedAt))
                  )
                )
            )
          : h(EmptyState, { title: "Nada em aberto", description: "Todos os pedidos estão concluídos ou desfeitos." })
      )
    )
  );
}

export function ResumoExecutivo(): ReactElement {
  return h(DatasetBoundary, {
    children: (dataset: DashboardDataset) =>
      h(PeriodOrdersBoundary, {
        defaultPeriod: "mes",
        children: (orders: OrdersDataset, context) => h(ResumoExecutivoView, { dataset, orders, toolbar: context.toolbar })
      })
  });
}
